import { useState } from "react";
import { PanelLeft } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { updateBuyerModuleSidebarPreference, useUiPreferences } from "@/lib/useUiPreferences";

/**
 * Toggle de barra lateral en módulos operativos (preferencia global del espacio).
 * Se muestra en Panel General → Herramientas para titular, comprador y diseñador.
 */
export default function BuyerSidebarPreferenceToggle() {
  const { buyerModuleSidebarEnabled, loading } = useUiPreferences();
  const [saving, setSaving] = useState(false);

  const onToggle = async (checked) => {
    setSaving(true);
    try {
      await updateBuyerModuleSidebarPreference(checked);
      toast.success(
        checked
          ? "Barra lateral activada: visible en Panel General y módulos (Visor, Laboral, etc.)."
          : "Barra lateral desactivada: vista limpia con tarjetas (sin menú lateral).",
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar la preferencia");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <div
      className="mt-4 rounded-md border border-slate-200 bg-white shadow-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      data-testid="buyer-sidebar-preference-toggle"
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className="rounded-md bg-slate-100 p-2 shrink-0">
          <PanelLeft className="w-5 h-5 text-[#0F2D5C]" strokeWidth={1.8} />
        </div>
        <div>
          <Label htmlFor="buyer-module-sidebar-switch" className="text-sm font-semibold text-[#0F2D5C]">
            Barra lateral en módulos operativos
          </Label>
          <p className="text-xs text-slate-600 mt-1 max-w-xl leading-relaxed">
            Cuando está desactivada (recomendado), la navegación usa las tarjetas del hub y la barra superior.
            Cuando está activa, muestra el menú lateral del módulo activo (Panel General y módulos operativos).
            En el módulo Diseñador la barra lateral sigue visible para soporte BNT.
          </p>
        </div>
      </div>
      <Switch
        id="buyer-module-sidebar-switch"
        checked={buyerModuleSidebarEnabled}
        disabled={loading || saving}
        onCheckedChange={onToggle}
        data-testid="buyer-module-sidebar-switch"
      />
    </div>
    {buyerModuleSidebarEnabled ? (
      <p
        className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2"
        data-testid="buyer-sidebar-preference-active-hint"
      >
        Preferencia activa. El menú lateral aparece contraído (solo iconos); pulse el chevron para
        expandirlo. También puede usar «Mostrar menú» en la barra superior dentro de un módulo.
      </p>
    ) : null}
  </>
  );
}
